import {
  Sheet,
  SheetTitle,
  SheetHeader,
  SheetTrigger,
  SheetInlineContent,
  SheetDescription,
} from '@design-system/Sheet';

import {
  getFoodRankMap,
  type FoodRankMap,
} from '@/utils/nutrition/getFoodRankMap';

import {
  useGetRankQueryV2,
  useGetAllFoodQueryV2,
  useGetSupplementListQuery,
} from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';

import type {
  GetAllFoodsResponse,
  ISupplementListResponse,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { cn } from '@/utils/styles';
import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import { Input } from '@design-system/Input';
import { forwardRef, useCallback, useState, useEffect } from 'react';
import { Button } from '@design-system/Button';
import { SearchIcon, XIcon } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { Skeleton } from '@design-system/Skeleton';
import { MyFoodsCard } from '@design-system/Cards/MyFoodsCard';
import { sanitizeString } from '@/utils/string/sanitizeString';
import { cva, type VariantProps } from 'class-variance-authority';
import { MY_FOODS_PATH, FORMULA_PATH } from '@/constants/paths';
import { Card, CardContent, CardHeader } from '@design-system/Card';
import { MY_FOODS_BADGE, MY_INGREDIENTS_BADGE } from '@/constants/images';
import { MyIngredientsCard } from '@design-system/Cards/MyIngredientsCard';
import { EventTracker } from '@/utils';

const DEFAULT_SECTIONS = [
  { label: 'My Foods', to: MY_FOODS_PATH, imgSrc: MY_FOODS_BADGE },
  { label: 'My Ingredients', to: FORMULA_PATH, imgSrc: MY_INGREDIENTS_BADGE },
];

const searchBarVariants = cva(
  'relative min-h-10 min-w-40 px-3 py-2 text-tertiary flex flex-row items-center justify-start gap-2 whitespace-nowrap rounded-lg typography-btn-large transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-primary-subtle ring-offset-2 disabled:pointer-events-none disabled:bg-overlay-primary disabled:text-tertiary [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-overlay-primary [&_svg]:text-primary',
        flat: 'bg-transparent text-primary hover:bg-overlay-secondary active:bg-overlay-secondary hover:text-secondary active:text-secondary',
        glass:
          'bg-background-blur-medium shadow-glass-image-inner bg-clip-padding backdrop-filter backdrop-blur-md',
        compact:
          '[&_span]:hidden bg-transparent rounded-full size-9 hover:bg-accent hover:text-accent-foreground min-w-fit',
      },
      responsive: {
        true: 'max-md:[&_span]:hidden max-md:bg-transparent max-md:rounded-full max-md:size-9 max-md:hover:bg-accent max-md:hover:text-accent-foreground max-md:min-w-fit max-md:shadow-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type SearchBarProps = React.ComponentProps<'div'>;

const renderRecentSearches = (
  recentSearches: string[] | undefined,
  onClick: (search: string) => void,
  onRemove: (search: string) => void,
) => {
  if (!Array.isArray(recentSearches)) return null;

  const renderedRecentSearches = recentSearches.map((search) => (
    <li key={search} className="flex flex-row gap-4">
      <Button
        variant="flat"
        onClick={() => onClick(search)}
        className="flex flex-row gap-2 w-full justify-start [&_svg]:!size-5 p-0 text-primary truncate"
      >
        <SearchIcon size={20} className="size-6 text-tertiary" />
        {search}
      </Button>

      <Button variant="ghost" size="icon" onClick={() => onRemove(search)}>
        <XIcon size={20} />
      </Button>
    </li>
  ));

  return renderedRecentSearches;
};

const renderSearchedFoods = (
  search: string,
  isLoading?: boolean,
  response?: GetAllFoodsResponse,
  foodRankMap?: FoodRankMap,
  handleSearchSubmit?: () => void,
) => {
  if (isLoading) {
    return Array.from({ length: 12 }).map(() => (
      <Skeleton className="w-full h-28" />
    ));
  }

  if (!response || !foodRankMap) return null;

  const renderedFoods = response.reduce((acc, food) => {
    const sanitizedFoodName = sanitizeString(food.title, { lowerCase: true });

    if (sanitizedFoodName.includes(search)) {
      acc.push(
        <SheetTrigger key={food.id} asChild onClick={handleSearchSubmit}>
          <MyFoodsCard
            food={food}
            variant="flat"
            className="h-28"
            rankMap={foodRankMap}
          />
        </SheetTrigger>,
      );
    }

    return acc;
  }, [] as React.ReactNode[]);

  return renderedFoods;
};

const renderSearchedIngredients = (
  search: string,
  isLoading?: boolean,
  response?: ISupplementListResponse,
  handleSearchSubmit?: () => void,
) => {
  if (isLoading) {
    return Array.from({ length: 12 }).map(() => (
      <Skeleton className="w-full h-28" />
    ));
  }

  if (!response) return null;

  const renderedIngredients = response.reduce((acc, supplement) => {
    const { ingredients } = supplement.formula;
    const productId = supplement.product_id;

    ingredients.forEach((ingredient) => {
      const sanitizeIngredientName = sanitizeString(ingredient.name, {
        lowerCase: true,
      });

      if (!sanitizeIngredientName.includes(search)) return;

      acc.push(
        <SheetTrigger key={ingredient.id} asChild onClick={handleSearchSubmit}>
          <MyIngredientsCard
            className="h-28"
            ingredient={ingredient}
            productId={productId}
          />
        </SheetTrigger>,
      );
    });

    return acc;
  }, [] as React.ReactNode[]);

  return renderedIngredients;
};

export const SearchBar = ({ children }: SearchBarProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const sanitizedSearch = sanitizeString(searchTerm, { lowerCase: true });

  const getRankQuery = useGetRankQueryV2();

  const enableQueries = Boolean(searchTerm.trim());

  const getAllFoodQuery = useGetAllFoodQueryV2();
  const getSupplementListQuery = useGetSupplementListQuery();

  const foodRankMap = getFoodRankMap(getRankQuery.data);

  const isLoading =
    getRankQuery.isLoading ||
    getAllFoodQuery.isLoading ||
    getSupplementListQuery.isLoading;

  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ searchBar: true }, '');
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
    } else {
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('overflowY');
    }

    const handlePopState = (event: PopStateEvent) => {
      if (isOpen) {
        event.preventDefault();
        setIsOpen(false);
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('width');
        document.body.style.removeProperty('overflowY');
      }
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      handleClearSearch();
    }
  };

  const handleOnRemoveRecentSearch = useCallback((search: string) => {
    setRecentSearches((searches) =>
      searches.filter((value) => value !== search),
    );
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setInputValue('');
  }, []);

  const handleSearchSubmit = useCallback(() => {
    if (
      inputValue.trim() &&
      !recentSearches.some((search) => search === inputValue)
    ) {
      setRecentSearches((searches) => [...searches, inputValue]);
    }
  }, [inputValue, recentSearches]);

  const debouncedSetSearchTerm = useDebounce({
    method: (value: string) => {
      setSearchTerm(value);
    },
  });

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    event.preventDefault();
    const searchFieldValue = event.target.value;

    setInputValue(searchFieldValue);
    debouncedSetSearchTerm(searchFieldValue);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleClickRecentSearch = useCallback((search: string) => {
    setSearchTerm(search);
    setInputValue(search);
  }, []);

  const renderedSearchedFoods = renderSearchedFoods(
    sanitizedSearch,
    isLoading,
    getAllFoodQuery.data,
    foodRankMap,
    handleSearchSubmit,
  );

  const renderedSearchedIngredients = renderSearchedIngredients(
    sanitizedSearch,
    isLoading,
    getSupplementListQuery.data,
    handleSearchSubmit,
  );

  const renderedDefaultSections = DEFAULT_SECTIONS.map((section) => (
    <SheetTrigger asChild key={section.label}>
      <Link variant="flat" to={section.to}>
        <Card className="w-full shadow-glass-image-inner">
          <CardHeader className="flex flex-row justify-center p-4">
            <Image
              width={96}
              height={48}
              aria-hidden
              className="h-12 w-24"
              src={section.imgSrc}
            />
          </CardHeader>

          <CardContent className="text-center text-secondary typography-caption1 p-4 pt-0">
            {section.label}
          </CardContent>
        </Card>
      </Link>
    </SheetTrigger>
  ));

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      {children}

      <SheetInlineContent
        side="center"
        variant="glass"
        className="!m-0 !rounded-none !h-full md:group-data-[open=true]/sidebar-wrapper:left-[--sidebar-width] max-md:group-data-[open-mobile=true]/sidebar-wrapper:left-[--sidebar-mobile-width] md:group-data-[open=true]/sidebar-wrapper:w-auto max-md:group-data-[open-mobile=true]/sidebar-wrapper:w-auto"
      >
        <div className="w-full mx-auto gap-6 page-container">
          <SheetHeader className="px-2 sm:px-4">
            <SheetTitle hidden>Universal Search</SheetTitle>

            <div className="relative">
              <Input
                autoFocus
                name="search"
                variant="secondary"
                value={inputValue}
                onChange={handleOnChange}
                onKeyDown={handleKeyDown}
                className="relative px-12 h-10 border-none"
              >
                <SearchIcon size={20} className="absolute left-4 !size-5" />

                {inputValue && (
                  <Button
                    size="icon"
                    type="button"
                    variant="flat"
                    onClick={handleClearSearch}
                    className="absolute rounded-md right-2 [&_svg]:!size-3"
                  >
                    <XIcon size={16} />
                  </Button>
                )}
              </Input>
            </div>
          </SheetHeader>

          <SheetDescription asChild>
            <div className="px-2 sm:px-4 flex flex-col gap-4">
              {!enableQueries && (
                <div className="flex flex-col gap-4 md:gap-6">
                  <span className="typography-title md:typography-h3 text-primary">
                    Go to
                  </span>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {renderedDefaultSections}
                  </ul>
                </div>
              )}

              {!enableQueries && !!recentSearches.length && (
                <div>
                  <span className="mb-8 typography-title md:typography-h3 text-primary">
                    Recent searches
                  </span>

                  <ul className="flex flex-col gap-2">
                    {renderRecentSearches(
                      recentSearches,
                      handleClickRecentSearch,
                      handleOnRemoveRecentSearch,
                    )}
                  </ul>
                </div>
              )}

              {enableQueries && !!renderedSearchedFoods?.length && (
                <div className="flex flex-col gap-3">
                  <span className="typography-body text-secondary">
                    Foods and Ingredients
                  </span>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {renderedSearchedFoods}
                    {renderedSearchedIngredients}
                  </ul>
                </div>
              )}

              {enableQueries && !renderedSearchedFoods?.length && (
                <div className="flex flex-col gap-2 justify-center items-center text-center">
                  <span className="text-secondary typography-h2">
                    Your search for "{searchTerm}" didn't return any results
                  </span>

                  <span className="text-tertiary typography-body">
                    Try another search with a new keyword
                  </span>
                </div>
              )}
            </div>
          </SheetDescription>
        </div>
      </SheetInlineContent>
    </Sheet>
  );
};

export interface SearchBarTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SheetTrigger>,
    VariantProps<typeof searchBarVariants> {
  placeholder?: React.ReactNode;
}

export const SearchBarTrigger = forwardRef<
  HTMLButtonElement,
  SearchBarTriggerProps
>(({ variant, className, responsive, placeholder = 'Search' }, ref) => {
  return (
    <SheetTrigger
      ref={ref}
      className={cn(searchBarVariants({ variant, responsive, className }))}
      onClick={() => {
        EventTracker.trackEvent('nutrition', 'nutrition_search_icon');
      }}
    >
      <SearchIcon size={20} className="!size-5" />

      <span className="truncate w-full flex flex-row items-center">
        {placeholder}
      </span>
    </SheetTrigger>
  );
});
